import { SearchOutlined } from '@mui/icons-material'
import { Box, Chip, InputAdornment, OutlinedInput, styled, ToggleButton, ToggleButtonGroup } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'

export type ReportTemplateSearchMode = 'schema' | 'template'

export interface ReportTemplateSearchBarProps {
  searchMode: ReportTemplateSearchMode
  searchValue: string
  onModeChange: (mode: ReportTemplateSearchMode) => void
  onSearchChange: (value: string) => void
}

export const ReportTemplateSearchBar: React.FC<ReportTemplateSearchBarProps> = ({
  searchMode,
  searchValue,
  onModeChange,
  onSearchChange,
}) => {
  const { t: translate } = useTranslation('dynamic-report')

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ReportTemplateSearchMode | null) => {
    if (newMode !== null) {
      onModeChange(newMode)
    }
  }

  return (
    <Box display="flex" alignItems="stretch" gap={0}>
      <SearchWrapper>
        <StyledInput
          value={searchValue}
          onChange={e => onSearchChange(e.target.value)}
          placeholder={translate(searchMode === 'schema' ? 'schema-search-placeholder' : 'template-search-placeholder')}
          fullWidth
          startAdornment={
            <InputAdornment position="start">
              <SearchOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          }
        />
      </SearchWrapper>
      <StyledToggleButtonGroup value={searchMode} exclusive onChange={handleModeChange} size="small">
        <StyledToggleButton value="schema">{translate('schema')}</StyledToggleButton>
        <StyledToggleButton value="template">{translate('template')}</StyledToggleButton>
      </StyledToggleButtonGroup>
    </Box>
  )
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: `${theme.palette.primary.main}1A`,
  borderRadius: '0 6px 6px 0',
  padding: '3px',
  gap: '2px',
  border: `1px solid ${theme.palette.inputSearch}`,
  '& .MuiToggleButtonGroup-grouped': {
    border: 'none',
    borderRadius: '4px !important',
  },
}))

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  color: theme.palette.primary.dark,
  fontWeight: 700,
  fontSize: '12px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  padding: theme.spacing(0.5, 1.5),
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}))

const SearchWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  width: '280px',
}))

const StyledInput = styled(OutlinedInput)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderRadius: '6px 0 0 6px',
  height: '36px',
  fontSize: '13px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
    borderRadius: '6px 0 0 6px',
    borderRightColor: 'transparent',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
    borderRightColor: 'transparent',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
    borderRightColor: 'transparent',
  },
}))

const ModeChip = styled(Chip)(({ theme }) => ({
  height: '20px',
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  backgroundColor: `${theme.palette.primary.main}1A`,
  color: theme.palette.primary.main,
  border: `1.5px solid ${theme.palette.primary.main}`,
  borderRadius: '4px',
  marginLeft: theme.spacing(0.75),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiChip-label': {
    padding: '0 6px',
    lineHeight: 1,
  },
}))
